/**
 * Apple TV → Get Started. The screen the section was missing.
 *
 * Clicking "Apple TV" used to land on a Screens table with a Pair button asking
 * for a 6-digit code, and nothing anywhere said that the code comes from an app
 * nobody had installed yet. The first step of the flow was invisible; this is it.
 *
 * NOTHING HERE IS AN EDIT ACTION, which is why the gate is looser than its
 * sibling pages: a user without `canManage` still reads every instruction, and
 * only the "Go to Screens" hand-off is withheld. Someone who cannot change what
 * plays can still be the person standing next to the television.
 */
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, ExternalLink } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { AppleTvGate, AppleTvPageShell, shouldGate, useAppleTvAccess } from './useAppleTvAccess';
import { APP_STORE_URL, APPLE_TV_ROUTES } from './appleTvSetup';
import { PairingScreenPreview } from './PairingScreenPreview';

/**
 * Wraps the app icon in a link to the store listing, when one exists.
 *
 * Renders its children bare when APP_STORE_URL is null, so the card looks the
 * same and nothing hints at a link that would go nowhere.
 */
function AppStoreLink({ children }: { children: React.ReactNode }) {
  if (!APP_STORE_URL) return <>{children}</>;

  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noreferrer"
      title="View ProsperaSign on the App Store"
      className="block rounded-lg transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {children}
    </a>
  );
}

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
        {number}
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <div className="mt-1 space-y-2 text-sm text-slate-600">{children}</div>
      </div>
    </li>
  );
}

export default function AppleTvGetStartedPage() {
  const access = useAppleTvAccess();

  if (shouldGate(access)) {
    return (
      <AppleTvPageShell>
        <div className="shrink-0 p-6 pb-0">
          <PageHeader title="Set up a screen" />
        </div>
        <AppleTvGate {...access} />
      </AppleTvPageShell>
    );
  }

  return (
    <AppleTvPageShell>
      {/* The same padded header wrapper every sibling page uses. Without it the
          title sat flush against the sidebar with its first letter clipped. */}
      <div className="shrink-0 p-6 pb-0">
        <PageHeader
          title="Set up a screen"
          subtitle="Install the ProsperaSign app on your Apple TV, then pair it here. It takes about five minutes."
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
        {/* Capped but NOT centred. The width cap is for readability — instructions
            read badly at full width on a wide monitor — while the left edge has
            to line up with the page heading above, which is not centred. */}
        <div className="w-full max-w-5xl space-y-8">
          <div
            role="note"
            className="flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
            <div className="space-y-2 text-sm">
              <p className="font-semibold">Important: Keep Your Apple TV Awake</p>
              <p>
                To ensure your playlist continues playing without interruption, configure the
                following settings on your Apple TV:
              </p>
              <ul className="list-disc space-y-0.5 pl-5">
                <li>
                  <b>Screen Saver:</b> Settings &rarr; Screen Saver &rarr; Start After &rarr; Never
                </li>
                <li>
                  <b>Sleep:</b> Settings &rarr; General &rarr; Sleep After &rarr; Never
                </li>
              </ul>
              <p>
                These settings help prevent Apple TV from interrupting your playlist or
                automatically going to sleep.
              </p>
              <p>
                <b>Note:</b> Also ensure your television&rsquo;s own power-saving and auto-sleep
                settings are disabled.
              </p>
            </div>
          </div>

          <div className="grid gap-8 lg:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
            {/* Visual first: the icon is how the app is recognised in the store. */}
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                {/* tvOS icons are LANDSCAPE 5:3. Cropping to a square would stop
                    it matching what is in the store, which is this image's job.

                    The icon opens the listing when there is one. Still a
                    REFERENCE, not the install route — the amber note below says
                    why, and a web link cannot be opened on the television. */}
                <AppStoreLink>
                  <img
                    src="/apple-tv/franchisesign-app-icon.png"
                    alt="ProsperaSign app icon"
                    className="w-full rounded-lg border border-slate-200"
                    style={{ aspectRatio: '5 / 3', objectFit: 'cover' }}
                  />
                </AppStoreLink>
                <h2 className="mt-3 text-base font-semibold text-slate-900">ProsperaSign</h2>
                <p className="text-sm text-slate-600">Digital signage player for Apple TV</p>
                <p className="mt-1 text-xs text-slate-500">Apple TV (tvOS) only &middot; Free</p>

                <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  This app is only on the Apple TV App Store. It will not appear if you search on
                  an iPhone or iPad.
                </p>
              </div>

              <PairingScreenPreview />
            </div>

            {/* The steps. */}
            <ol className="space-y-6">
              <Step number={1} title="Install the app on your Apple TV">
                <ol className="list-decimal space-y-1 pl-4">
                  <li>On your Apple TV, go to the App Store.</li>
                  <li>Select the search icon and type ProsperaSign.</li>
                  <li>Choose the app with the icon shown here and select Get to install it.</li>
                </ol>

                {/* Rendered only when a real listing URL exists — see appleTvSetup.ts.
                    A web link is useless on the television itself, so this is a
                    reference for a phone or computer, never the install route. */}
                {APP_STORE_URL && (
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                  >
                    View on the App Store <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </Step>

              <Step number={2} title="Open the app">
                <p>
                  Open ProsperaSign on the Apple TV. It shows a QR code and a large 6-digit
                  pairing code. Leave this screen up &mdash; you will need that code in a moment.
                  If the code disappears or expires, the app shows a new one automatically.
                </p>
              </Step>

              <Step number={3} title="Add the location">
                <p>
                  Every screen belongs to a location. If the site this TV is in isn&rsquo;t listed
                  yet, add it first.
                </p>
                <Link to={APPLE_TV_ROUTES.locations} className="mt-3 inline-block">
                  <Button size="sm" className="min-w-[10rem]" rightIcon={<ArrowRight className="h-4 w-4" />}>
                    Go to Locations
                  </Button>
                </Link>
              </Step>

              {/* Between the location and the pairing on purpose: pairing asks
                  which playlist to show, so arriving there without one means
                  stopping halfway to go and build it. The sub-steps name the
                  actual buttons ("Upload", "Add Playlist", "Add media") rather
                  than describing them, so they can be followed by eye. */}
              <Step number={4} title="Add a playlist">
                <p>
                  A playlist is what the screen actually shows &mdash; the images and videos, in
                  the order they play. Build one before pairing, because pairing asks which
                  playlist to use.
                </p>
                {/* Each hand-off sits with the sub-step it belongs to rather
                    than in a row underneath: two buttons at the foot of a
                    three-item list give no clue which one goes with which, and
                    these are done in order. */}
                <ol className="mt-2 list-decimal space-y-3 pl-4">
                  <li>
                    Go to Media and select Upload to add your images and videos.
                    {access.canManage && (
                      <Link to={APPLE_TV_ROUTES.media} className="mt-2 block">
                        <Button
                          size="sm"
                          className="min-w-[10rem]"
                          rightIcon={<ArrowRight className="h-4 w-4" />}
                        >
                          Go to Media
                        </Button>
                      </Link>
                    )}
                  </li>
                  <li>
                    Go to Playlists, select Add Playlist and give it a name.
                    {access.canManage && (
                      <Link to={APPLE_TV_ROUTES.playlists} className="mt-2 block">
                        <Button
                          size="sm"
                          className="min-w-[10rem]"
                          rightIcon={<ArrowRight className="h-4 w-4" />}
                        >
                          Go to Playlists
                        </Button>
                      </Link>
                    )}
                  </li>
                  <li>
                    Open the playlist, select Add media, and choose what plays and in what order.
                  </li>
                  <li>
                    <span className="font-medium text-slate-900">Optional: schedule an item.</span>{' '}
                    Give it a start date, an end date or both, and it plays only on those days, for
                    example a seasonal promotion. Both dates are included.
                    <ul className="mt-1 list-disc space-y-0.5 pl-5">
                      <li>Start date only: it plays from that day on.</li>
                      <li>End date only: it plays until that day, then stops.</li>
                      <li>No dates: it always plays.</li>
                    </ul>
                  </li>
                </ol>
              </Step>

              <Step number={5} title="Pair the screen">
                <p>
                  Go to Screens, select Pair Device, and enter the 6-digit code from the TV. Give
                  the screen a name, pick its location and choose a playlist. The Apple TV starts
                  playing within a few seconds.
                </p>
                {/* The only thing withheld from a view-only user: everything above
                    is information, this one starts a change. */}
                {access.canManage && (
                  <Link to={APPLE_TV_ROUTES.screens} className="mt-3 inline-block">
                    <Button size="sm" className="min-w-[10rem]" rightIcon={<ArrowRight className="h-4 w-4" />}>
                      Go to Screens
                    </Button>
                  </Link>
                )}
              </Step>
            </ol>
          </div>

          <details className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <summary className="cursor-pointer text-sm font-semibold text-slate-900">
              Having trouble?
            </summary>
            <dl className="mt-3 space-y-3 text-sm">
              {[
                ['The code on the TV stopped working', 'Codes expire. Read the new code off the TV screen and try again.'],
                ['The TV still shows the pairing screen', 'Check the Apple TV is on the internet, then confirm the screen shows as Online under Screens.'],
                ['I can’t find the app', 'Make sure you are searching the App Store on the Apple TV itself, not on a phone or computer.'],
                ['Nothing is playing after pairing', 'The screen needs a playlist assigned. Check Screens, then Playlists.'],
              ].map(([q, a]) => (
                <div key={q}>
                  <dt className="font-medium text-slate-800">{q}</dt>
                  <dd className="text-slate-600">{a}</dd>
                </div>
              ))}
            </dl>
          </details>
        </div>
      </div>
    </AppleTvPageShell>
  );
}
